import type { BankDeposit, BankLine, UnidentifiedReceipt } from "../../services/financialService";
import { StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Views, sorts and columns for the three banking registers: statement lines
 * to reconcile, money banked, and money received from an unknown payer.
 */

const money = (value: string | number) => Number(value || 0).toLocaleString();
const signed = (value: string | number) => {
  const amount = Number(value || 0);
  return <span className={amount < 0 ? "text-red-700" : "text-emerald-700"}>{amount < 0 ? "−" : "+"}{Math.abs(amount).toLocaleString()}</span>;
};

// Statement lines ---------------------------------------------------------------

export const lineStatus = (line: BankLine, bestScore?: number) =>
  line.matched_entry
    ? { label: `Matched · ${line.matched_entry_number ?? ""}`.trim(), dot: "bg-emerald-500" }
    : bestScore
      ? { label: `Unmatched · ${bestScore}% suggestion`, dot: "bg-amber-500" }
      : { label: "Unmatched", dot: "bg-gray-400" };

export function bankLineRegister(bankName: (id: number) => string, bestScore: (line: BankLine) => number | undefined) {
  const config: RegisterConfig<BankLine> = {
    search: (line) => [line.description, line.reference, line.matched_entry_number, line.matched_entry_description, bankName(line.bank_account), line.import_filename].join(" "),
    views: [
      { key: "unmatched", label: "Unmatched", match: (line) => !line.matched_entry },
      { key: "matched", label: "Matched", match: (line) => Boolean(line.matched_entry) },
      { key: "in", label: "Money in", match: (line) => Number(line.amount) > 0 },
      { key: "out", label: "Money out", match: (line) => Number(line.amount) < 0 },
      { key: "all", label: "All" },
    ],
    date: { label: "Date", value: (line) => line.transaction_date },
    sorts: [
      { key: "-date", label: "Newest first", compare: reversed(byText<BankLine>((line) => line.transaction_date)) },
      { key: "date", label: "Oldest first", compare: byText<BankLine>((line) => line.transaction_date) },
      { key: "-size", label: "Largest amount first", compare: reversed(byNumber<BankLine>((line) => Math.abs(Number(line.amount)))) },
    ],
  };
  const columns: RegisterColumn<BankLine>[] = [
    { header: "Date", cell: (line) => line.transaction_date, exportValue: (line) => line.transaction_date, className: "tabular-nums text-gray-700" },
    { header: "Bank", cell: (line) => bankName(line.bank_account), exportValue: (line) => bankName(line.bank_account), className: "text-gray-700" },
    { header: "Description", cell: (line) => line.description, exportValue: (line) => line.description, className: "max-w-[18rem] truncate text-gray-900" },
    { header: "Reference", cell: (line) => <span className="font-mono text-xs text-gray-600">{line.reference || "—"}</span>, exportValue: (line) => line.reference },
    { header: "Amount (UGX)", cell: (line) => <span className="font-semibold">{signed(line.amount)}</span>, exportValue: (line) => Number(line.amount), align: "right" },
    { header: "Status", cell: (line) => <StatusDot {...lineStatus(line, bestScore(line))} />, exportValue: (line) => lineStatus(line).label },
  ];
  return { config, columns };
}

// Money banked ----------------------------------------------------------------

export const depositStatus = (deposit: BankDeposit) =>
  deposit.cancelled_at
    ? { key: "cancelled", label: "Cancelled", dot: "bg-gray-400" }
    : deposit.on_statement
      ? { key: "on", label: "On the statement", dot: "bg-emerald-500" }
      : { key: "waiting", label: "Not on a statement yet", dot: "bg-amber-500" };

export const depositConfig: RegisterConfig<BankDeposit> = {
  search: (deposit) => [deposit.reference, deposit.source_account_name, deposit.bank_account_name, deposit.notes, deposit.entry_number, deposit.recorded_by_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "waiting", label: "Not on a statement yet", match: (deposit) => depositStatus(deposit).key === "waiting" },
    { key: "on", label: "On the statement", match: (deposit) => depositStatus(deposit).key === "on" },
    { key: "cancelled", label: "Cancelled", match: (deposit) => Boolean(deposit.cancelled_at) },
  ],
  date: { label: "Banked", value: (deposit) => deposit.deposit_date },
  sorts: [
    { key: "-date", label: "Newest first", compare: reversed(byText<BankDeposit>((deposit) => deposit.deposit_date)) },
    { key: "date", label: "Oldest first", compare: byText<BankDeposit>((deposit) => deposit.deposit_date) },
    { key: "-amount", label: "Amount (highest first)", compare: reversed(byNumber<BankDeposit>((deposit) => deposit.amount)) },
  ],
};

export const depositColumns: RegisterColumn<BankDeposit>[] = [
  { header: "Banked", cell: (deposit) => deposit.deposit_date, exportValue: (deposit) => deposit.deposit_date, className: "tabular-nums text-gray-700" },
  { header: "From", cell: (deposit) => deposit.source_account_name, exportValue: (deposit) => deposit.source_account_name, className: "max-w-[12rem] truncate text-gray-700" },
  { header: "Into", cell: (deposit) => deposit.bank_account_name, exportValue: (deposit) => deposit.bank_account_name, className: "text-gray-700" },
  { header: "Reference", cell: (deposit) => <span className="font-mono text-xs text-gray-600">{deposit.reference}</span>, exportValue: (deposit) => deposit.reference },
  { header: "Amount (UGX)", cell: (deposit) => <span className="font-semibold text-gray-900">{money(deposit.amount)}</span>, exportValue: (deposit) => Number(deposit.amount), align: "right" },
  { header: "Status", cell: (deposit) => <StatusDot {...depositStatus(deposit)} />, exportValue: (deposit) => depositStatus(deposit).label },
];

// Unidentified money -------------------------------------------------------------

export const unidentifiedStatus = (receipt: UnidentifiedReceipt) =>
  receipt.status === "resolved"
    ? { label: "Matched to a student", dot: "bg-emerald-500" }
    : receipt.status === "refunded"
      ? { label: "Returned to payer", dot: "bg-gray-400" }
      : { label: "Unresolved", dot: "bg-amber-500" };

export const unidentifiedConfig: RegisterConfig<UnidentifiedReceipt> = {
  search: (receipt) => [receipt.reference, receipt.payer_name, receipt.notes, receipt.student_name, receipt.invoice_number, receipt.receipt_account_name].join(" "),
  views: [
    { key: "unresolved", label: "Unresolved", match: (receipt) => receipt.status === "unresolved" },
    { key: "resolved", label: "Matched", match: (receipt) => receipt.status === "resolved" },
    { key: "refunded", label: "Returned", match: (receipt) => receipt.status === "refunded" },
    { key: "all", label: "All" },
  ],
  date: { label: "Received", value: (receipt) => receipt.received_at },
  sorts: [
    { key: "-date", label: "Newest first", compare: reversed(byText<UnidentifiedReceipt>((receipt) => receipt.received_at)) },
    { key: "date", label: "Oldest first", compare: byText<UnidentifiedReceipt>((receipt) => receipt.received_at) },
    { key: "-amount", label: "Amount (highest first)", compare: reversed(byNumber<UnidentifiedReceipt>((receipt) => receipt.amount)) },
  ],
};

export const unidentifiedColumns: RegisterColumn<UnidentifiedReceipt>[] = [
  { header: "Received", cell: (receipt) => receipt.received_at?.slice(0, 10), exportValue: (receipt) => receipt.received_at?.slice(0, 10) ?? "", className: "tabular-nums text-gray-700" },
  { header: "Reference", cell: (receipt) => <span className="font-mono text-xs text-gray-800">{receipt.reference}</span>, exportValue: (receipt) => receipt.reference },
  { header: "Payer shown", cell: (receipt) => receipt.payer_name || <span className="text-gray-400">Unknown</span>, exportValue: (receipt) => receipt.payer_name ?? "", className: "max-w-[12rem] truncate" },
  { header: "Method", cell: (receipt) => humanise(receipt.payment_method), exportValue: (receipt) => humanise(receipt.payment_method), className: "text-gray-700" },
  { header: "Into", cell: (receipt) => receipt.receipt_account_name, exportValue: (receipt) => receipt.receipt_account_name, className: "text-gray-700" },
  { header: "Amount (UGX)", cell: (receipt) => <span className="font-semibold text-gray-900">{money(receipt.amount)}</span>, exportValue: (receipt) => Number(receipt.amount), align: "right" },
  { header: "Status", cell: (receipt) => <StatusDot {...unidentifiedStatus(receipt)} />, exportValue: (receipt) => unidentifiedStatus(receipt).label },
];
